import produce, { current } from 'immer';
import _ from 'lodash';
import { merge } from 'lodash/fp';
import { ActionStatus } from 'thunkless';

import * as UUID from '@/lib/uuid';
import { EventElementType } from '@/constants/event-element-type.enum';

import { AppAction } from '../actions';
import { EventTypes } from '../actions/event';
import { TicketTypes } from '../actions/ticket';
import { InboxTypes } from '../actions/inbox';

export interface EventState {
  data: {
    [ticketId: string]: {
      data: Record<string, any>;
      upwardsLoadingStatus: ActionStatus;
      downwardsLoadingStatus: ActionStatus;
      upwardsKey?: string;
      downwardsKey?: number;
    },
  },
  local: { // failed messages that exist only locally
    [ticketId: string]: {
      [timestamp: number]: Record<string, any>;
    }
  },
  // Maps real event_id to the temporary_id for in-place updates
  idAliases: {
    [ticketId: string]: {
      [eventId: string]: string;
    };
  };
}

export const initialState: EventState = {
  data: {},
  local: {},
  idAliases: {},
};

// `element_data.message_html` holds server-sanitized HTML and is fed to
// innerHTML/dangerouslySetInnerHTML downstream. The edit flows carry the new
// body as plain text, so escape it before it lands in that field — otherwise a
// body like `<b>bold</b>` renders as `bold` (and `<img onerror=...>` runs) until
// the websocket refresh replaces it with the server's value.
const escapeHtml = (text: string): string => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// A thunkless FAILURE action wraps the rejection as { error, origin }; for
// server rejections `error` is the jqXHR, whose responseJSON carries the
// controller's errors array. Network-level failures fall back to the caller's
// generic message.
const failureMessage = (payload: unknown, fallback: string): string =>
  (payload as any)?.error?.responseJSON?.errors?.join?.(', ') || fallback;

// Element types the ticket reducer treats as last activity
const LAST_ACTIVITY_TYPES = [EventElementType.SMS, EventElementType.MMS, EventElementType.CALL];

export const reducer = produce((state: EventState, action: AppAction) => {
  switch (action.type) {
    case TicketTypes.APPEND_TICKETS: {
      // Unloaded tickets keep one trimmed last-activity record for the ticket card; loaded ones use APPEND_EVENTS
      action.payload.forEach((item) => {
        const uuid = item.ticket?.uuid;
        const { element } = item;
        if (!uuid || state.data[uuid]?.upwardsLoadingStatus) return;
        if (!element?.event_id || !LAST_ACTIVITY_TYPES.includes(element.element_type)) return;
        _.set(state, ['data', uuid, 'data'], {
          [element.event_id]: {
            id: element.event_id,
            element_type: element.element_type,
            sent_at: element.sent_at,
            element_data: {
              direction: element.direction,
              sender_name: item.sender_name,
              sender: { first_name: item.sender_tag },
              sent_at: element.sent_at,
            },
          },
        });
      });
      break;
    }

    case EventTypes.APPEND_EVENTS: {
      /* eslint-disable @typescript-eslint/naming-convention */
      action.payload.forEach(
        ({ ticket, element, preview, sender_tag, sender_name, temporary_id }) => {
          const { uuid } = ticket;
          const {
            event_id, element_id, element_type, type, context,
            account_id, ticket_id, user_id, owner_id, sender_type,
            sender_id, call_id, broadcast_id, broadcast_name, direction, body,
            message, contents, media_tags_pending, segments, status, sent_at,
            error_description,
            scheduled_for, updated_at, aborted, sent, media_url, cancel_on, aborter_id,
            aborted_by, abort_reason, qa_keyword_id, rating, summary, model,
            actions, content_blocked, last_activity_direction,
          } = element;
          /* eslint-enable @typescript-eslint/naming-convention */
          if (!event_id) {
            console.log('element missing event_id', element);
            return;
          }

          const event = {
            id: event_id,
            element_id,
            element_type,
            sent_at,
            element_data: {
              id: element_id,
              sent_at,
            } as Record<string, any>,
          };
          switch (element_type) {
            case EventElementType.SMS:
            case EventElementType.MMS:
              Object.assign(event.element_data, {
                message_html: message,
                contents,
                media_tags_pending,
                direction,
                broadcast_id,
                broadcast_name,
                error_description,
                // Never present live; null keeps the previously fetched value in the merge below.
                pure_emoji_message: null,
                read_receipt: null,
                sanitized_error_text: null,
                segments,
                sender_tag,
                sender_name,
                sender: { first_name: sender_tag },
                status,
                content_blocked,
                last_activity_direction,
              });
              break;

            case EventElementType.PENDING_MESSAGE:
              Object.assign(event.element_data, {
                message_html: message,
                contents,
                sender_tag,
                media_url,
                scheduled_for,
                // The cancelled/sent bubble renders its timestamp from
                // updated_at; the merge below keeps the previous value when a
                // payload omits it.
                updated_at,
                cancel_on,
                aborted,
                sent,
                aborter_id,
                aborted_by,
                abort_reason,
                dirty: false,
              });
              break;

            case EventElementType.STATUS_CHANGE:
              Object.assign(event.element_data, {
                account_id,
                ticket_id,
                user_id,
                status,
                preview,
              });
              break;

            case EventElementType.OWNER_CHANGE:
              Object.assign(event.element_data, {
                account_id,
                ticket_id,
                user_id,
                owner_id,
                preview,
              });
              break;

            case EventElementType.NOTE:
              Object.assign(event.element_data, {
                ticket_id,
                call_id,
                sender_type,
                sender_id,
                body,
                sender_tag,
              });
              break;

            case EventElementType.CALL:
              Object.assign(event.element_data, {
                direction,
                sender_tag,
              });
              break;
            case EventElementType.CUSTOM_EVENT:
              Object.assign(event.element_data, {
                type,
                body: context,
                qa_keyword_id,
              });
              break;
            case EventElementType.SENTIMENT:
              Object.assign(event.element_data, {
                type,
                rating,
                summary,
                actions,
                model,
              });
              break;
          }

          // If a temporary entry exists, update it in-place so the React key stays stable
          // Otherwise use the real event_id
          const aliasedKey = (temporary_id && state.data[uuid]?.data?.[temporary_id] != null)
            ? temporary_id
            : state.idAliases[uuid]?.[event_id];
          const targetKey = aliasedKey ?? event_id;

          // Sparse live re-deliveries keep the previous value on null (except error fields, which the live payload always reports authoritatively) so re-delivery can't wipe enriched data or leave stale errors behind.
          const OVERWRITE_EVEN_WHEN_NULL = new Set(['sanitized_error_text', 'error_description']);
          const previous = state.data[uuid]?.data?.[targetKey];
          if (previous?.element_data) {
            event.element_data = _.mergeWith(
              {},
              previous.element_data,
              event.element_data,
              (previousValue, incomingValue, key) => {
                if (incomingValue == null && !OVERWRITE_EVEN_WHEN_NULL.has(key as string)) return previousValue;
                if (_.isArray(incomingValue)) return incomingValue;
                return undefined; // default deep merge
              },
            );
          }
          _.set(state, ['data', uuid, 'data', targetKey], event);

          if (aliasedKey === temporary_id && temporary_id) {
            state.idAliases[uuid] ??= {};
            state.idAliases[uuid][event_id] = temporary_id;
          }
        },
      );
      break;
    }

    case EventTypes.FETCH_EVENTS_REQUEST: {
      const { ticketUuid, direction } = action.meta;
      const loadingKey = direction === 'downwards' ? 'downwardsLoadingStatus' : 'upwardsLoadingStatus';
      _.set(state, ['data', ticketUuid, loadingKey], ActionStatus.BUSY);
      break;
    }

    case EventTypes.FETCH_EVENTS_SUCCESS: {
      const { data, lastKey, firstKey } = action.payload;
      const { ticketUuid, direction } = action.meta;
      _.update(state, ['data', ticketUuid, 'data'], merge(_.keyBy(data, 'id')));
      if (direction !== 'downwards') _.set(state, ['data', ticketUuid, 'upwardsKey'], lastKey);
      if (direction !== 'upwards') _.set(state, ['data', ticketUuid, 'downwardsKey'], firstKey);
      const loadingKey = direction === 'downwards' ? 'downwardsLoadingStatus' : 'upwardsLoadingStatus';
      _.set(state, ['data', ticketUuid, loadingKey], ActionStatus.SUCCESS);
      break;
    }

    case EventTypes.FETCH_EVENTS_FAILURE: {
      const { ticketUuid, direction } = action.meta;
      const loadingKey = direction === 'downwards' ? 'downwardsLoadingStatus' : 'upwardsLoadingStatus';
      _.set(state, ['data', ticketUuid, loadingKey], ActionStatus.FAILURE);
      break;
    }

    case EventTypes.REMOVE_MESSAGE: {
      const {
        ticketUuid, temporaryId,
      } = action.meta;
      delete state.data[ticketUuid].data[temporaryId];
      break;
    }

    case EventTypes.CREATE_MESSAGE_REQUEST:
    case EventTypes.SEND_MESSAGE_REQUEST: {
      const {
        message, scheduledFor, cancelOn,
        media, mediaType, mediaDimensions,
        temporaryId, ticketUuid, oldId, unsentMessage, created,
      } = action.meta;
      let elementType: string;
      if (scheduledFor) elementType = EventElementType.PENDING_MESSAGE;
      else if (media) elementType = EventElementType.MMS;
      else elementType = EventElementType.SMS;

      const element_data = {
        direction: 'out',
        message_html: message,
        scheduled_for: scheduledFor && Number(new Date(scheduledFor)) / 1000,
        cancel_on: cancelOn,
        contents: media && [{
          id: 0,
          url: media,
          content_type: mediaType,
          dimensions: mediaDimensions && JSON.parse(mediaDimensions),
        }],
        status: 'request',
        created,
      };
      if (unsentMessage) Object.assign(element_data, { unsentMessage });

      _.set(state, ['data', ticketUuid, 'data', temporaryId], {
        id: temporaryId,
        element_type: elementType,
        element_data,
      });
      if (oldId) {
        _.unset(state, ['data', ticketUuid, 'data', oldId]);
        Object.entries(state.local[ticketUuid]).forEach(([timestamp, { id }]) => {
          if (id !== oldId) return;
          _.unset(state, ['local', ticketUuid, timestamp]);
        });
      }
      break;
    }

    case EventTypes.SEND_MESSAGE_FAILURE: {
      const { temporaryId, ticketUuid, sender } = action.meta;
      const { error } = action.payload;
      const errorText = (error as any).readyState === 0 ? 'Network Error' : 'Unknown Error';
      const timestamp = Date.now();
      _.update(state, ['data', ticketUuid, 'data', temporaryId, 'element_data'], merge({
        sanitized_error_text: errorText,
        sent_at: timestamp / 1000,
        sender_tag: sender,
      }));
      _.set(state, ['data', ticketUuid, 'data', temporaryId, 'element_data', 'status'], 'client_failure');

      const event = _.get(state, ['data', ticketUuid, 'data', temporaryId]);
      state.local[ticketUuid] ??= {};
      _.set(state, ['local', ticketUuid, timestamp], event);

      break;
    }

    case EventTypes.ABORT_MESSAGE_REQUEST: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'abortStatus'], ActionStatus.BUSY);
      break;
    }
    case EventTypes.ABORT_MESSAGE_SUCCESS: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'abortStatus'], ActionStatus.SUCCESS);
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'aborted'], true);
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'abort_reason'], 'manually cancelled');
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'updated_at'], Date.now() / 1000);
      break;
    }
    case EventTypes.ABORT_MESSAGE_FAILURE: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'abortStatus'], ActionStatus.FAILURE);
      break;
    }

    case EventTypes.UPDATE_MESSAGE_REQUEST: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'updateStatus'], ActionStatus.BUSY);
      _.set(state, ['data', ticket, 'data', eventId, 'updateError'], null);
      break;
    }
    case EventTypes.UPDATE_MESSAGE_SUCCESS: {
      const { ticket, eventId, newValue, scheduledFor, cancelOn } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'updateStatus'], ActionStatus.SUCCESS);
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'dirty'], false);
      const base = ['data', ticket, 'data', eventId, 'element_data'];
      if (newValue !== undefined) _.set(state, [...base, 'message_html'], escapeHtml(newValue));
      if (scheduledFor !== undefined) _.set(state, [...base, 'scheduled_for'], Number(new Date(scheduledFor)) / 1000);
      if (cancelOn !== undefined) _.set(state, [...base, 'cancel_on'], cancelOn);
      break;
    }
    case EventTypes.UPDATE_MESSAGE_FAILURE: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'updateStatus'], ActionStatus.FAILURE);
      _.set(
        state, ['data', ticket, 'data', eventId, 'updateError'],
        failureMessage(action.payload, 'Unable to update this message. Please try again.'),
      );
      break;
    }

    case EventTypes.SEND_NOW_REQUEST: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'sendNowStatus'], ActionStatus.BUSY);
      _.set(state, ['data', ticket, 'data', eventId, 'sendNowError'], null);
      break;
    }
    case EventTypes.SEND_NOW_SUCCESS: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'sendNowStatus'], ActionStatus.SUCCESS);
      // Optimistically mark as sent; the delivered message arrives as its own
      // event over the websocket and the pending bubble hides itself.
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'sent', 'element_id'], 'sent');
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'updated_at'], Date.now() / 1000);
      break;
    }
    case EventTypes.SEND_NOW_FAILURE: {
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'sendNowStatus'], ActionStatus.FAILURE);
      _.set(
        state, ['data', ticket, 'data', eventId, 'sendNowError'],
        failureMessage(action.payload, 'Unable to send this message. Please try again.'),
      );
      break;
    }

    case EventTypes.EDIT_MESSAGE: {
      const newValue = action.payload;
      const { ticket, eventId } = action.meta;
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'message_html'], escapeHtml(newValue));
      _.set(state, ['data', ticket, 'data', eventId, 'element_data', 'dirty'], true);
      break;
    }

    case EventTypes.UPDATE_MMS_CONTENT_DIMENSIONS: {
      const { contentUrl, dimensions } = action.payload;
      const { ticket, eventId } = action.meta;
      const contents = state.data[ticket]?.data?.[eventId]?.element_data?.contents;
      if (!contents) break;

      const content = contents.find((x) => x.url === contentUrl);
      content.dimensions = dimensions;
      break;
    }

    case EventTypes.TOGGLE_CALL_NOTES: {
      const { ticket, eventId } = action.meta;
      _.update(state, ['data', ticket, 'data', eventId, 'element_data', 'areNotesOpen'], (v) => !v);
      break;
    }

    case EventTypes.TOGGLE_DATA_EVENTS: {
      const { ticket } = action.meta;

      const eventIds = Object.values(state.data[ticket].data).reduce((acc, e) => {
        if (e?.element_type === EventElementType.UNADDRESSED_CHANGE || e?.element_type === EventElementType.CUSTOM_EVENT) acc.push(e.id);
        return acc;
      }, []);

      eventIds.forEach((eid) => {
        _.update(state, ['data', ticket, 'data', eid, 'element_data', 'areDataEventsOn'], (v) => !v);
      });

      break;
    }

    case EventTypes.TOGGLE_SENTIMENT_EVENT_DETAILS: {
      const { ticket, eventId } = action.meta;
      _.update(state, ['data', ticket, 'data', eventId, 'element_data', 'show'], (v) => !v);
      break;
    }

    case EventTypes.EMAIL_TRANSCRIPT_REQUEST: {
      break;
    }

    case EventTypes.RESET_EVENTS: {
      const { ticket } = action.meta;
      state.data[ticket].upwardsKey = null;
      state.data[ticket].downwardsKey = null;
      break;
    }

    case EventTypes.SET_PAGINATION_KEY: {
      const { ticket } = action.meta;
      Object.assign(state.data[ticket], action.payload);
      break;
    }

    case InboxTypes.FETCH_INBOX_TICKETS_SUCCESS: {
      const { data } = action.payload;
      data.forEach(({ uuid, last_activity }) => {
        if (!last_activity) return;
        const { preview, ...lastActivityData } = last_activity;
        _.set(state, ['data', uuid, 'data', lastActivityData.id], lastActivityData);
      });
      break;
    }

    case TicketTypes.FETCH_TICKET_SUCCESS: {
      const { data: { tickets: [ticket] } } = action.payload;
      const { uuid } = action.meta;

      const { avo_notes: avoNotes, last_activity: lastActivity } = ticket;
      const notes = avoNotes.reduce((acc, { id, ...elementData }) => {
        const noteKey = `note_${id}`;
        acc[noteKey] = {
          id: noteKey,
          element_type: EventElementType.NOTE,
          element_data: { ...elementData, id },
        };
        return acc;
      }, {});
      _.update(state, ['data', uuid, 'data'], merge(notes));
      if (lastActivity) {
        const { preview, ...lastActivityData } = lastActivity;
        _.set(state, ['data', uuid, 'data', lastActivityData.id], lastActivityData);
      }
      break;
    }

    case TicketTypes.CREATE_NOTE_REQUEST: {
      const { temporaryId, note, ticket, senderTag } = action.meta;
      const sentAt = Math.floor(Date.now() / 1000);
      _.set(state, ['data', ticket, 'data', temporaryId], {
        id: temporaryId,
        element_type: EventElementType.NOTE,
        element_data: {
          body: note,
          sender_tag: senderTag,
          sent_at: sentAt,
        },
      });
      break;
    }
  }
}, initialState);
